"use client";

import { ReactionBar } from "@/components/reactions/ReactionBar";
import { BraveheartIcon, JokerIcon } from "@/components/ui/CustomIcons";
import {
  CombinedTournamentProvider,
  useMatchesContext,
  useTeamsContext,
  useTournamentContext,
} from "@/contexts/TournamentContext";
import { matchResultToString } from "@/lib/sports";
import { getTowerLevel } from "@/lib/tower";
import {
  ChallengeType,
  Location,
  Match,
  MatchStatus,
  Team,
  Tournament,
} from "@/types";
import {
  Autocomplete,
  AutocompleteItem,
  Card,
  CardBody,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Tabs,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import {
  format,
  isAfter,
  isThisWeek,
  isToday,
  isTomorrow,
  isYesterday,
} from "date-fns";
import { Locale as DateFnsLocale, enUS, fi } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

interface MatchSchedulePageClientProps {
  tournament: Tournament;
}

interface GroupedMatch {
  date: string;
  dateLabel: string;
  matches: ScheduledMatchWithDetails[];
}

interface ScheduledMatchWithDetails {
  match: Match;
  team1: Team | undefined;
  team2: Team | undefined;
  team1Players: string[];
  team2Players: string[];
  team1Level: number | null;
  team2Level: number | null;
  location: Location | undefined;
}

export default function MatchSchedulePageClient({
  tournament,
}: MatchSchedulePageClientProps) {
  return (
    <CombinedTournamentProvider tournamentId={tournament.id}>
      <MatchScheduleContent />
    </CombinedTournamentProvider>
  );
}

type MatchTab = "upcoming" | "past";

function MatchScheduleContent() {
  const t = useTranslations();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const {
    tournament,
    locations,
    players,
    loading: tournamentLoading,
  } = useTournamentContext();
  const { teams, loading: teamsLoading } = useTeamsContext();
  const { matches, loading: matchesLoading } = useMatchesContext();
  const loading = tournamentLoading || teamsLoading || matchesLoading;
  const [selectedTab, setSelectedTab] = useState<MatchTab>("upcoming");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("all");
  const [teamFilter, setTeamFilter] = useState<string>("");

  // Initialize team filter and tab from URL parameters
  useEffect(() => {
    const teamParam = searchParams.get("team");
    if (teamParam) {
      setTeamFilter(teamParam);
    }
    const tabParam = searchParams.get("tab");
    if (tabParam === "past" || tabParam === "upcoming") {
      setSelectedTab(tabParam);
    }
  }, [searchParams]);

  const dateLocale = locale === "fi" ? fi : enUS;

  // Create teams map for lookups
  const teamsMap = useMemo(
    () => new Map(teams.map((team) => [team.id, team])),
    [teams],
  );

  // Create locations map for lookups
  const locationsMap = useMemo(
    () => new Map(locations.map((location) => [location.id, location])),
    [locations],
  );

  // Compute team level map (teamId → level) from ranking-sorted positions
  const teamLevelMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!tournament) return map;
    const sorted = [...teams]
      .filter((t) => t.isActive && t.isPlacedInTower !== false)
      .sort((a, b) => a.ranking - b.ranking);
    sorted.forEach((team, index) => {
      const position = index + 1;
      map.set(
        team.id,
        getTowerLevel(
          position,
          tournament.towerTeamsPerRow,
          tournament.towerTopRowTeamCount,
        ),
      );
    });
    return map;
  }, [teams, tournament]);

  // Filter function for team name
  const matchesTeamFilter = (match: Match): boolean => {
    if (!teamFilter.trim()) return true;
    const searchLower = teamFilter.toLowerCase().trim();
    const team1 = teamsMap.get(match.team1Id);
    const team2 = teamsMap.get(match.team2Id);
    const team1Name = team1?.name?.toLowerCase() || "";
    const team2Name = team2?.name?.toLowerCase() || "";
    return team1Name.includes(searchLower) || team2Name.includes(searchLower);
  };

  // Filter upcoming matches (scheduled, future date)
  const upcomingMatches = useMemo(() => {
    const now = new Date();

    return matches
      .filter(
        (match) =>
          match.status === MatchStatus.SCHEDULED &&
          match.matchDate &&
          isAfter(new Date(match.matchDate), now),
      )
      .filter((match) =>
        selectedLocationId === "all"
          ? true
          : match.locationId === selectedLocationId,
      )
      .filter(matchesTeamFilter)
      .sort((a, b) => {
        const dateA = a.matchDate ? new Date(a.matchDate).getTime() : 0;
        const dateB = b.matchDate ? new Date(b.matchDate).getTime() : 0;
        return dateA - dateB;
      });
  }, [matches, selectedLocationId, teamFilter, teamsMap]);

  // Filter past matches (completed with result)
  const pastMatches = useMemo(() => {
    return matches
      .filter((match) => match.result !== undefined && match.result !== null)
      .filter((match) =>
        selectedLocationId === "all"
          ? true
          : match.locationId === selectedLocationId,
      )
      .filter(matchesTeamFilter)
      .sort((a, b) => {
        const dateA = a.matchDate ? new Date(a.matchDate).getTime() : 0;
        const dateB = b.matchDate ? new Date(b.matchDate).getTime() : 0;
        return dateB - dateA; // Most recent first
      });
  }, [matches, selectedLocationId, teamFilter, teamsMap]);

  // Get active matches based on selected tab
  const activeMatches =
    selectedTab === "upcoming" ? upcomingMatches : pastMatches;

  // Add team and location details to matches
  const matchesWithDetails: ScheduledMatchWithDetails[] = useMemo(() => {
    return activeMatches.map((match) => {
      const team1 = teamsMap.get(match.team1Id);
      const team2 = teamsMap.get(match.team2Id);
      return {
        match,
        team1,
        team2,
        team1Players: (team1?.playerIds ?? [])
          .map((id) => players.get(id)?.name)
          .filter(Boolean) as string[],
        team2Players: (team2?.playerIds ?? [])
          .map((id) => players.get(id)?.name)
          .filter(Boolean) as string[],
        team1Level: teamLevelMap.get(match.team1Id) ?? null,
        team2Level: teamLevelMap.get(match.team2Id) ?? null,
        location: match.locationId
          ? locationsMap.get(match.locationId)
          : undefined,
      };
    });
  }, [activeMatches, teamsMap, locationsMap, players, teamLevelMap]);

  // Group matches by date
  const groupedMatches: GroupedMatch[] = useMemo(() => {
    const groups = new Map<string, ScheduledMatchWithDetails[]>();

    matchesWithDetails.forEach((item) => {
      if (!item.match.matchDate) return;

      const matchDate = new Date(item.match.matchDate);
      const dateKey = format(matchDate, "yyyy-MM-dd");

      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(item);
    });

    // Sort groups by date (ascending for upcoming, descending for past)
    const sortedEntries = Array.from(groups.entries()).sort((a, b) => {
      const dateA = new Date(a[0]).getTime();
      const dateB = new Date(b[0]).getTime();
      return selectedTab === "upcoming" ? dateA - dateB : dateB - dateA;
    });

    return sortedEntries.map(([dateKey, matches]) => {
      const matchDate = new Date(matches[0].match.matchDate!);

      let dateLabel: string;
      if (isToday(matchDate)) {
        dateLabel = t("matchSchedule.today");
      } else if (selectedTab === "upcoming" && isTomorrow(matchDate)) {
        dateLabel = t("matchSchedule.tomorrow");
      } else if (selectedTab === "past" && isYesterday(matchDate)) {
        dateLabel = t("matchSchedule.yesterday");
      } else if (isThisWeek(matchDate, { weekStartsOn: 1 })) {
        // Show weekday name for this week
        dateLabel = format(matchDate, "EEEE", { locale: dateLocale });
      } else {
        // Show full date for other dates
        dateLabel = format(matchDate, "EEEE, d. MMMM", { locale: dateLocale });
      }

      return {
        date: dateKey,
        dateLabel,
        matches,
      };
    });
  }, [matchesWithDetails, t, dateLocale, selectedTab]);

  // Show location filter only if tournament has more than one location
  const showLocationFilter = locations.length > 1;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const emptyStateMessage =
    selectedTab === "upcoming"
      ? {
          title: t("matchSchedule.noUpcomingMatches"),
          description: t("matchSchedule.noUpcomingMatchesDescription"),
        }
      : {
          title: t("matchSchedule.noPastMatches"),
          description: t("matchSchedule.noPastMatchesDescription"),
        };

  return (
    <div className="w-full space-y-6">
      {/* Tabs */}
      <div className="flex justify-center">
        <Tabs
          selectedKey={selectedTab}
          onSelectionChange={(key) => setSelectedTab(key as MatchTab)}
          aria-label={t("matchSchedule.matchTabs")}
          color="warning"
          variant="solid"
          classNames={{
            tabList:
              "gap-1 p-1.5 bg-warning/5 rounded-xl border border-warning/30",
            tab: "px-5 py-2.5 h-auto font-semibold text-default-500 data-[selected=true]:text-warning",
            cursor: "bg-warning shadow-md",
          }}
        >
          <Tab
            key="upcoming"
            title={
              <div className="flex items-center gap-2">
                <Icon icon="solar:calendar-bold" width={18} />
                <span>{t("matchSchedule.upcoming")}</span>
                {upcomingMatches.length > 0 && (
                  <span className="text-xs bg-warning/20 text-warning px-1.5 py-0.5 rounded-full font-semibold">
                    {upcomingMatches.length}
                  </span>
                )}
              </div>
            }
          />
          <Tab
            key="past"
            title={
              <div className="flex items-center gap-2">
                <Icon icon="solar:history-bold" width={18} />
                <span>{t("matchSchedule.past")}</span>
                {pastMatches.length > 0 && (
                  <span className="text-xs bg-warning/20 text-warning px-1.5 py-0.5 rounded-full font-semibold">
                    {pastMatches.length}
                  </span>
                )}
              </div>
            }
          />
        </Tabs>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Team Autocomplete */}
        <Autocomplete
          label={t("matchSchedule.searchTeam")}
          placeholder={t("matchSchedule.searchTeamPlaceholder")}
          inputValue={teamFilter}
          onInputChange={setTeamFilter}
          allowsCustomValue
          startContent={
            <Icon
              icon="solar:magnifer-linear"
              className="text-default-400"
              width={18}
            />
          }
          isClearable
          onClear={() => setTeamFilter("")}
          className="flex-1"
          size="sm"
          classNames={{
            base: "[&_[data-slot=input-wrapper]]:bg-content1/60 [&_[data-slot=input-wrapper]]:border [&_[data-slot=input-wrapper]]:border-default-200/50",
          }}
          items={teams.map((team) => ({ id: team.id, name: team.name }))}
        >
          {(team) => (
            <AutocompleteItem key={team.id}>{team.name}</AutocompleteItem>
          )}
        </Autocomplete>

        {/* Location Filter */}
        {showLocationFilter && (
          <Select
            label={t("matchSchedule.filterByLocation")}
            selectedKeys={[selectedLocationId]}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as string;
              setSelectedLocationId(value || "all");
            }}
            className="sm:w-56 shrink-0"
            size="sm"
            aria-label={t("matchSchedule.filterByLocation")}
            classNames={{
              trigger: "bg-content1/60 border border-default-200/50",
            }}
            items={[{ id: "all", name: t("common.all") }, ...locations]}
          >
            {(item) => <SelectItem key={item.id}>{item.name}</SelectItem>}
          </Select>
        )}
      </div>

      {/* Match List */}
      {groupedMatches.length === 0 ? (
        <Card className="bg-content1/50">
          <CardBody className="flex flex-col items-center justify-center py-12 text-center">
            <Icon
              icon={
                selectedTab === "upcoming"
                  ? "solar:calendar-mark-line-duotone"
                  : "solar:history-line-duotone"
              }
              className="text-default-300 mb-4"
              width={64}
            />
            <h3 className="text-lg font-medium text-default-600">
              {emptyStateMessage.title}
            </h3>
            <p className="text-default-400 mt-2 max-w-md">
              {emptyStateMessage.description}
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-8">
          {groupedMatches.map((group) => (
            <div key={group.date}>
              {/* Date Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2 text-primary">
                  <Icon icon="solar:calendar-bold" width={20} />
                  <h2 className="text-lg font-semibold capitalize">
                    {group.dateLabel}
                  </h2>
                </div>
                <div className="flex-1 h-px bg-default-200" />
                <span className="text-sm text-default-400">
                  {t("matchSchedule.matchCount", {
                    count: group.matches.length,
                  })}
                </span>
              </div>

              {/* Matches for this date */}
              <div className="space-y-3">
                {group.matches.map((item) => (
                  <MatchCard
                    key={item.match.id}
                    item={item}
                    dateLocale={dateLocale}
                    showResult={selectedTab === "past"}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface MatchCardProps {
  item: ScheduledMatchWithDetails;
  dateLocale: DateFnsLocale;
  showResult?: boolean;
}

function MatchCard({ item, dateLocale, showResult = false }: MatchCardProps) {
  const t = useTranslations();
  const { players } = useTournamentContext();
  const {
    match,
    team1,
    team2,
    team1Players,
    team2Players,
    team1Level,
    team2Level,
    location,
  } = item;

  const matchTime = match.matchDate
    ? format(new Date(match.matchDate), "HH.mm", { locale: dateLocale })
    : null;

  const team1Name = team1?.name || t("common.unnamedTeam");
  const team2Name = team2?.name || t("common.unnamedTeam");

  // Determine winner for past matches
  const winnerId = match.result?.winnerId;
  const team1IsWinner = winnerId === match.team1Id;
  const team2IsWinner = winnerId === match.team2Id;

  const isJoker = match.challengeType === ChallengeType.JOKER;
  const isBraveheart = match.challengeType === ChallengeType.BRAVEHEART;
  const hasCard = isJoker || isBraveheart;

  const isUpcoming = !showResult;

  const playerNames = useMemo(() => {
    const map = new Map<string, string>();
    players.forEach((p, id) => map.set(id, p.name));
    return map;
  }, [players]);

  // team1 = challenger (arrow up), team2 = challenged (arrow down)
  const team1TextClass = showResult
    ? team1IsWinner
      ? "text-success"
      : "text-default-400"
    : "text-foreground";

  const team2TextClass = showResult
    ? team2IsWinner
      ? "text-success"
      : "text-default-400"
    : "text-foreground";

  return (
    <Card
      className={`overflow-hidden ${
        isUpcoming
          ? "bg-content1/60 border border-primary/20 hover:border-primary/40 transition-all"
          : "bg-content1/60 border border-default-200/50 hover:border-primary/30 transition-colors"
      }`}
    >
      <CardBody className="p-0">
        <div className="flex">
          {/* Left accent bar for upcoming matches */}
          {isUpcoming && (
            <div className="w-1 shrink-0 bg-gradient-to-b from-primary via-secondary to-primary" />
          )}

          <div className="flex-1 px-3 py-2.5 sm:px-4 sm:py-3 space-y-1.5">
            {/* Header row: time + location + card badge */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Icon
                icon={
                  isUpcoming
                    ? "solar:clock-circle-bold"
                    : "solar:check-circle-bold"
                }
                className={`${isUpcoming ? "text-primary" : "text-success"} shrink-0`}
                width={20}
              />
              <span
                className={`text-lg font-bold shrink-0 ${isUpcoming ? "text-primary" : "text-default-600"}`}
              >
                {matchTime}
              </span>

              {location && (
                <>
                  <span className="text-default-300 shrink-0">|</span>
                  <div className="flex items-center gap-1 text-sm text-default-500 min-w-0">
                    <Icon
                      icon="solar:map-point-bold"
                      width={14}
                      className="shrink-0"
                    />
                    <span className="truncate">{location.name}</span>
                  </div>
                </>
              )}

              <div className="flex-1" />

              {isJoker && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-secondary/10 text-secondary text-xs font-medium shrink-0">
                  <JokerIcon className="text-sm" />
                  <span className="hidden sm:inline">
                    {t("matchSchedule.jokerChallenge")}
                  </span>
                </span>
              )}
              {isBraveheart && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-danger/10 text-danger text-xs font-medium shrink-0">
                  <BraveheartIcon className="text-sm" />
                  <span className="hidden sm:inline">
                    {t("matchSchedule.braveheartChallenge")}
                  </span>
                </span>
              )}
            </div>

            {/* Score row (past matches only) */}
            {showResult && match.result && (
              <div className="flex justify-end">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-warning/10 text-warning text-sm font-semibold font-mono">
                  {matchResultToString(match.result, match.sport)}
                </span>
              </div>
            )}

            {/* Teams: arrow | team col | vs | team col | arrow */}
            <div className="flex items-start gap-1 sm:gap-2">
              {/* Arrow up: only when challenger won (positions swapped) */}
              {team1IsWinner && (
                <Icon
                  icon="solar:arrow-up-linear"
                  className="text-success shrink-0 mt-2"
                  width={26}
                />
              )}

              {/* Team 1 (challenger) column */}
              <div className="flex-1 min-w-0 text-right">
                <div className="flex items-baseline justify-end gap-1">
                  {team1IsWinner && (
                    <Icon
                      icon="solar:cup-bold"
                      className="text-warning shrink-0 relative top-0.5"
                      width={14}
                    />
                  )}
                  <span
                    className={`text-sm sm:text-base font-semibold truncate ${team1TextClass}`}
                  >
                    {team1Name}
                  </span>
                </div>
                {team1Level != null && (
                  <span className="text-tiny md:text-sm text-primary/70 leading-none shrink-0">
                    {t("tower.level", { level: team1Level })}
                  </span>
                )}
                {team1Players.length > 0 && (
                  <p className="text-[11px] text-default-400 break-words">
                    {team1Players.join(", ")}
                  </p>
                )}
              </div>

              {/* VS divider */}
              <span className="text-xs sm:text-sm text-default-400 uppercase font-medium shrink-0 px-1.5 sm:px-3 mt-0.5">
                {t("matches.vs")}
              </span>

              {/* Team 2 (challenged) column */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1">
                  <span
                    className={`text-sm sm:text-base font-semibold truncate ${team2TextClass}`}
                  >
                    {team2Name}
                  </span>
                  {team2IsWinner && (
                    <Icon
                      icon="solar:cup-bold"
                      className="text-warning shrink-0 relative top-0.5"
                      width={14}
                    />
                  )}
                </div>
                {team2Level != null && (
                  <span className="text-tiny md:text-sm text-primary/70 leading-none shrink-0">
                    {t("tower.level", { level: team2Level })}
                  </span>
                )}
                {team2Players.length > 0 && (
                  <p className="text-[11px] text-default-400 break-words">
                    {team2Players.join(", ")}
                  </p>
                )}
              </div>

              {/* Arrow down: only when challenger won (positions swapped) */}
              {team1IsWinner && (
                <Icon
                  icon="solar:arrow-down-linear"
                  className="text-danger shrink-0 mt-2"
                  width={26}
                />
              )}
            </div>

            {showResult && (
              <ReactionBar
                matchId={match.id}
                reactions={match.reactions}
                userNames={playerNames}
              />
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
